import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wrench,
  RefreshCw,
  Fan,
  Wind,
  Thermometer,
  Droplets,
  ArrowRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: Wrench,
    title: "AC Repair",
    desc: "Not cooling, short cycling, or strange noises? Diagnostic intake before the truck rolls.",
    to: "/services/ac-repair",
    accent: "from-aqua/20 to-aqua/0",
  },
  {
    icon: RefreshCw,
    title: "Maintenance / Tune-Up",
    desc: "Seasonal tune-ups built for Florida heat &mdash; coils, drains, filters, and refrigerant checks.",
    to: "/services/maintenance",
    accent: "from-aqua/15 to-aqua/0",
  },
  {
    icon: Fan,
    title: "System Replacement",
    desc: "When repair stops adding up &mdash; right-sized swap-outs for SWFL square footage and humidity.",
    to: "/services/replacement",
    accent: "from-sun/20 to-sun/0",
  },
  {
    icon: Wind,
    title: "Indoor Air Quality",
    desc: "Dehumidification, filtration, and UV options for homes that fight humidity year round.",
    to: "/services/indoor-air-quality",
    accent: "from-aqua/15 to-aqua/0",
  },
  {
    icon: Thermometer,
    title: "Thermostat Issues",
    desc: "Smart thermostat setup, wiring quirks, and getting your schedule actually working.",
    to: "/services",
    accent: "from-aqua/15 to-aqua/0",
  },
  {
    icon: Droplets,
    title: "Weak Airflow & Leaks",
    desc: "Drain line backups, dripping units, and rooms that just never cool down like they should.",
    to: "/services",
    accent: "from-sun/15 to-sun/0",
  },
];

export default function ServiceCards() {
  return (
    <section
      data-testid="services-section"
      aria-labelledby="services-heading"
      className="relative bg-mist py-20 sm:py-24"
    >
      <div className="container-page">
        <div className="max-w-2xl">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
            What we cover
          </span>
          <h2
            id="services-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-[2.75rem]"
          >
            HVAC services, mapped to how homeowners actually describe problems.
          </h2>
          <p className="mt-4 text-base text-navy/70 sm:text-lg">
            Six core service types &mdash; each with a tailored intake flow so
            the contractor walks in already knowing what to bring.
          </p>
        </div>

        <motion.ul
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            show: {
              transition: { staggerChildren: 0.07, delayChildren: 0.05 },
            },
          }}
          className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {SERVICES.map((service) => {
            const Icon = service.icon;
            const slug = service.title
              .toLowerCase()
              .replace(/&/g, "and")
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            return (
              <motion.li
                key={service.title}
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
                }}
              >
                <Link
                  to={service.to}
                  data-testid={`service-card-${slug}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-mist-line bg-white p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-aqua/40 hover:shadow-cyanGlow"
                >
                  <div
                    className={`pointer-events-none absolute inset-x-0 -top-px h-32 bg-gradient-to-b ${service.accent} opacity-80`}
                    aria-hidden="true"
                  />
                  <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-aqua shadow-soft">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="relative mt-5 font-display text-xl font-bold tracking-tight text-navy">
                    {service.title}
                  </h3>
                  <p
                    className="relative mt-2 text-sm leading-relaxed text-navy/70"
                    dangerouslySetInnerHTML={{ __html: service.desc }}
                  />
                  <span className="relative mt-5 inline-flex items-center gap-1 text-sm font-semibold text-aqua-deep transition-transform group-hover:translate-x-0.5">
                    Learn more
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
}
